// MA-C21 — `shape_array` renderer for ms.math.00f shape-identification items
// ("Which shape is on the card?").
//
// THE GAP THIS CLOSES: these 25 pre-chapter items rendered BLANK — there was
// no shape renderer, and the authored `pw_00f_*` imageRef has no backing
// asset, so the card the item asks about was never drawn.
//
// WHAT IT DRAWS: the authored `targetShape` filled with `targetColor`.
// `displayStyle` selects a drawing FAMILY (outline vs filled vs small/large)
// — see shapeGlyphs.tsx's styleFamily() header for why this is threaded
// through, not dropped. The visual IS the question (read-the-shape, the same
// "the visual IS the question" class as NumeralCardRenderer/ClockRenderer) —
// showing it is faithful, not a leak.
//
// ANSWER-SAFE by construction: the mapper (contentToEngineItem.ts) only ever
// emits this kind when `shape` already equals the item's correct answer —
// this component just draws whatever shape/color/style it is given.
//
// ADDITIVE — registered in rendererRegistry.tsx beside the other built-ins.
import type { ReactElement } from "react";
import type { MathRendererProps } from "./rendererRegistry";
import { colorToken, styleFamily, shapeGlyph, isKnownShape } from "./shapeGlyphs";

export default function ShapeArrayRenderer({ shape, color, displayStyle }: MathRendererProps): ReactElement {
  if (!isKnownShape(shape)) {
    // Fail-safe: never throw, never draw a misleading blank/wrong shape.
    return <svg viewBox="0 0 10 10" width="0" height="0" role="img" aria-label="Shape card" />;
  }
  const family = styleFamily(displayStyle);
  const size = family === "small" ? 42 : family === "large" ? 66 : 54;
  const fill = colorToken(color);
  const outlineWidth = displayStyle === "thick outline" ? 9 : 5;
  return (
    <svg viewBox="0 0 160 160" width="100%" style={{ maxWidth: 160 }} role="img"
      aria-label={`A card showing a ${shape}.`}>
      <rect x="4" y="4" width="152" height="152" rx="18"
        fill="var(--pal-cream, #faf3e8)" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="3" />
      {shapeGlyph(shape, 80, 80, size, fill, family, "s", outlineWidth)}
    </svg>
  );
}
